import React from "react";
import styled from "styled-components";
import { Typography } from "antd";
import { WarningFilled } from "@ant-design/icons";
import { SbTooltip } from "uiCore";

const { Text } = Typography;

// Column-cell rendering of "Critical Skills Gaps to Bridge" — a compact
// stacked list, not the full description text from Figma, since this lives
// inside a dense table cell.

const ListStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SkillRowStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${(p) => p.theme?.colors?.dark ?? "#2B3034"};
`;

const NONE_LABEL = "All skills matched";

// Column filter value for a CriticalSkillTags cell: every priority skill name
// (including any collapsed into "+N more"), or the same text the empty cell shows.
export const getPrioritySkillsFilterValue = (criticalGaps = []) =>
  criticalGaps.length ? criticalGaps.map((s) => s.skillName) : NONE_LABEL;

// No default limit -- role-successor-analytics and next-roles (see
// normalizeCriticalSkills) return every one of the role's importance == 5
// ("Critical") skills the candidate has a gap on, and that's genuinely
// unbounded (a JD can rate any number of its skills Critical), so there's
// no fixed count to truncate to. `limit` stays available as an explicit
// opt-in for a caller that wants one (e.g. the mock data fallback,
// getCriticalSkillGaps, isn't bounded by the backend the same way).
const CriticalSkillTags = ({ criticalGaps = [], limit = Infinity }) => {
  // Empty either because the JD has no importance == 5 skills at all, or
  // because it does but this candidate has no gap on any of them — both
  // read the same way to the viewer: nothing here needs bridging.
  if (!criticalGaps.length) return <Text style={{ fontSize: 12 }}>{NONE_LABEL}</Text>;

  const shown = criticalGaps.slice(0, limit);
  const remaining = criticalGaps.length - shown.length;

  return (
    <ListStyled>
      {shown.map((s) => (
        // required/current are only populated when the caller is
        // role-successor-analytics (see normalizeCriticalSkills) — disabled
        // leaves every other caller's plain name-only tag exactly as before.
        <SbTooltip
          key={s.skillId}
          disabled={s.current == null || s.required == null}
          content={`Current proficiency ${s.current} / Required ${s.required}`}
        >
          <SkillRowStyled>
            <WarningFilled style={{ color: "#D97706", fontSize: 11 }} />
            {s.skillName}
          </SkillRowStyled>
        </SbTooltip>
      ))}
      {remaining > 0 && <Text style={{ fontSize: 12 }}>+{remaining} more</Text>}
    </ListStyled>
  );
};

export default CriticalSkillTags;
